import joi from 'joi';
import {
  CampaignCardComponentAttributes,
  CampaignCardComponentI18nAttributes,
  CampaignCardOpenType,
  CardLayouts,
  ChannelDisplayType,
} from '@loyalty/editor-components';
import {
  ChannelId,
  ChannelSearchResult,
  SearchElementType,
  SearchLanguage,
} from '@qualifio/data-search-api-client';
import { ChannelType } from '@loyalty/shared-common/engage-manager-api-client';
import { FormGroup } from '@mui/material';
import { Module } from '@loyalty/shared-common/organisation-api-client';
import { useFormContext } from 'react-hook-form';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataSearchField } from '../../../../shared/forms/fields/DataSearchField';
import { InputTextField } from '../../../../shared/forms/fields/InputTextField';
import { SelectField } from '../../../../shared/forms/fields/SelectField';
import { Trait } from '../utils/Trait';
import { TraitFormValue } from '../types';
import { getCardLayoutTemplateSelectOptions } from '../utils/card-layout-template-options';
import { useAccounts } from '../../../../accounts/AccountsContext';
import { useApi } from '../../../../shared/api-client/ApiClientProvider';
import { useEditorStore } from '../../../store';
import { useProgram } from '../../../../loyalty/ProgramContext';

export const campaignCardTraitsSchema = joi.object<TraitFormValue>({
  attributes: joi.object<CampaignCardComponentAttributes>({
    template: joi.string(),
    openType: joi.string().valid(...Object.values(CampaignCardOpenType)),
  }),
  i18nAttributes: joi.object<CampaignCardComponentI18nAttributes>({
    channelUuid: joi.string(),
    campaignId: joi.number(),
    title: joi.string().optional().empty('').allow(null),
    image: joi.string().optional().empty('').allow(null),
    description: joi.string().optional().empty('').allow(null),
    ctaLabel: joi.string().optional().empty('').allow(null),
  }),
});

type CampaignCardTraitsType = TraitFormValue<
  CampaignCardComponentAttributes,
  CampaignCardComponentI18nAttributes
>;

export function CampaignCardTraits() {
  const { t } = useTranslation();
  const { watch, control } = useFormContext<CampaignCardTraitsType>();
  const { currentAccount } = useAccounts();
  const { programId } = useProgram();

  // Use Zustand store
  const locale = useEditorStore(state => state.locale);

  const { dataSearchApi } = useApi();
  const previousI18nAttributes = watch('i18nAttributes');

  const openType = watch('attributes.openType');

  const campaignOpenOptions = useMemo(
    () => [
      {
        id: ChannelDisplayType.modal,
        name: t('editor.campaign.traits.channelType.options.modal'),
      },
      {
        id: ChannelDisplayType.newTab,
        name: t('editor.campaign.traits.channelType.options.new-tab'),
      },
      {
        id: ChannelDisplayType.iframe,
        name: t('editor.campaign.traits.channelType.options.iframe'),
      },
    ],
    [t],
  );

  const cardLayoutTemplateOptions = useMemo(() => getCardLayoutTemplateSelectOptions(t), [t]);

  return (
    <FormGroup>
      <Trait>
        <DataSearchField<CampaignCardTraitsType>
          dataSearchApiClient={dataSearchApi}
          name="i18nAttributes"
          module={Module.LOYALTY}
          externalTenantId={currentAccount.id}
          entityId={programId}
          searchCriteria={{ channel: { type: ChannelType.WIDGET } }}
          size="sm"
          languages={[locale.toUpperCase() as SearchLanguage]}
          searchResultToValue={result => {
            if (!result) {
              return undefined;
            }
            const channel = (result as ChannelSearchResult).data;
            return {
              ...previousI18nAttributes,
              campaignId: channel.campaign.id,
              channelUuid: channel.productionId,
            };
          }}
          valueToSearchResult={(value): ChannelId | undefined => {
            const channelUuid = (value as CampaignCardComponentI18nAttributes)?.channelUuid;
            return channelUuid
              ? {
                  type: SearchElementType.channel,
                  uniqueId: channelUuid,
                }
              : undefined;
          }}
        />
      </Trait>
      <Trait>
        <SelectField
          control={control}
          name="attributes.openType"
          label={t('editor.campaign.traits.channelType.label')}
          defaultValue={CampaignCardOpenType.modal}
          options={campaignOpenOptions}
        />
      </Trait>
      {openType !== CampaignCardOpenType.iframe && (
        <Trait>
          <SelectField
            control={control}
            name="attributes.template"
            label={t('editor.card-layout.label')}
            defaultValue={CardLayouts.DEFAULT}
            options={cardLayoutTemplateOptions}
          />
        </Trait>
      )}
      <Trait>
        <InputTextField<CampaignCardTraitsType>
          name="i18nAttributes.image"
          label={t('editor.campaign-card.traits.image.label')}
          placeholder="https://example.com/image.png"
          debounceMs={300}
        />
      </Trait>
      <Trait>
        <InputTextField<CampaignCardTraitsType>
          name="i18nAttributes.title"
          label={t('editor.campaign-card.traits.title.label')}
          placeholder={t('editor.campaign-card.traits.title.placeholder')}
          debounceMs={300}
        />
      </Trait>
      <Trait>
        <InputTextField<CampaignCardTraitsType>
          name="i18nAttributes.description"
          multiline={true}
          rows={3}
          label={t('editor.campaign-card.traits.description.label')}
          placeholder={t('editor.campaign-card.traits.description.placeholder')}
          debounceMs={300}
        />
      </Trait>
      <Trait>
        <InputTextField<CampaignCardTraitsType>
          name="i18nAttributes.ctaLabel"
          label={t('editor.campaign-card.traits.ctaLabel.label')}
          placeholder={t('editor.campaign-card.traits.ctaLabel.placeholder')}
          debounceMs={300}
        />
      </Trait>
    </FormGroup>
  );
}
